// Copyright (c) StrangeBee 2024

import { AuditOutput } from 'thehive-sdk';
import client from '@Client';
import { DynamicObjectType } from '@Types';

type Subscriber = (audits: AuditOutput[]) => void;
export interface ScopeProps {
    rootId?: string;
    objectType?: string;
}

const NAMESPACE = '[STREAM]';

/**
 * event's list to use
 */
export const STREAM_EVENTS = {
    ANY: 'any',
    CASE: 'case',
    TASK: 'task',
    LOG: 'log',
    ANALYZER: 'job', //updated V0->V1
    ALERT: 'alert',
    MISP: 'misp',
    OBSERVABLE: 'observable',
    USER: 'user',
    AUDIT: 'audit',
    CASETEMPLATE: 'caseTemplate',
    CUSTOMFIELD: 'customField',
    COMMENT: 'comment',
    OBSERVABLETYPE: 'observableType',
    ORGANISATION: 'organisation',
    PROFILE: 'profile',
    DASHBOARD: 'dashboard',
    PAGE: 'page',
    ACTION: 'action',
    PROCEDURE: 'procedure',
};

/**
 * @param {string} rootId
 * @param {string} objectType
 * @returns {string}
 */
function computeEventName({ rootId = STREAM_EVENTS.ANY, objectType = STREAM_EVENTS.ANY }: ScopeProps): string {
    return `${NAMESPACE}:${rootId}-${objectType}`.toLocaleLowerCase();
}

class StreamSrv {
    abortController?: AbortController;
    subscribers: Record<string, Set<Subscriber>> = {};

    start(): Promise<void> {
        this.abortController = new AbortController();

        return client.StreamController.getStream({
            onmessage: (audit) => {
                this._onMessage(audit);
            },
            signal: this.abortController.signal,
            openWhenHidden: true,
            onclose: () => {
                this.start();
            },
            onerror: (err) => {
                if (err?.cause?.status == 401) {
                    throw err;
                }
                // else wait for 5 seconds before retrying
                return 5000;
            },
            onopen: (response: Response) => {
                const contentType = response.headers.get('content-type') || '';
                if (response.status == 200 && contentType.startsWith('text/event-stream')) {
                    return Promise.resolve();
                } else if (response.status != 200) {
                    throw new Error('Bad status code', { cause: response });
                } else {
                    throw new Error("Bad content-type, expected 'text/event-stream'", { cause: response });
                }
            },
        });
    }

    cancel() {
        this.abortController?.abort();
    }

    addObserver(scope: ScopeProps, subscriber: Subscriber): () => void {
        const eventName = computeEventName(scope);
        if (!(eventName in this.subscribers)) {
            this.subscribers[eventName] = new Set();
        }
        this.subscribers[eventName].add(subscriber);

        return () => {
            if (eventName in this.subscribers) {
                this.subscribers[eventName].delete(subscriber);
            }
        };
    }

    private _onMessage(audit: AuditOutput) {
        const messageByEventName = this._transformMessage(audit);
        Object.keys(messageByEventName).forEach((eventName) => {
            const subs = this.subscribers[eventName] || new Set();
            const audits = messageByEventName[eventName];
            subs.forEach((callback) => callback(audits));
        });
    }

    private _transformMessage(message: AuditOutput): Record<string, AuditOutput[]> {
        const {
            base: { rootId = STREAM_EVENTS.ANY, objectType = STREAM_EVENTS.ANY },
            summary,
        } = message;
        const output: Record<string, AuditOutput[]> = {};

        const listEventName = [
            computeEventName({ objectType: STREAM_EVENTS.ANY }), // any event
            computeEventName({ rootId }), // any TypeEvent
            computeEventName({ objectType }), // any Id
            computeEventName({ rootId, objectType }), // specific Id and Type
        ];

        Object.keys(summary).forEach((secondaryObjectType) => {
            if (secondaryObjectType !== objectType) {
                listEventName.push(computeEventName({ objectType: secondaryObjectType }));
            }
        });

        listEventName.forEach((eventName: string) => {
            output[eventName] = output[eventName] ? [...output[eventName], message] : [message];
        });

        return output;
    }
}

interface StreamApiOption {
    onCatch?: (er: any) => any;
    feedInterceptor?: (result: GetFeedType) => void;
    pollingDuration?: number;
}

export interface GetFeedType {
    data: AuditOutput[];
    status: number;
}

class StreamLongPollingSrv {
    subscribers: Record<string, Set<Subscriber>> = {};
    streamId: string | null = null;
    isPolling = false;
    pollingDuration = 0;
    onCatch: (er: any) => any = () => undefined;
    feedInterceptor: (result: any) => void = () => undefined;

    addObserver(scope: ScopeProps, subscriber: Subscriber): () => void {
        const eventName = computeEventName(scope);
        if (!(eventName in this.subscribers)) {
            this.subscribers[eventName] = new Set();
        }
        this.subscribers[eventName].add(subscriber);

        return () => {
            if (eventName in this.subscribers) {
                this.subscribers[eventName].delete(subscriber);
            }
        };
    }

    initialise(option?: StreamApiOption): Promise<void> | null {
        if (this.streamId !== null) {
            return null;
        }

        if (option?.onCatch) {
            this.onCatch = option.onCatch;
        }
        if (option?.feedInterceptor) {
            this.feedInterceptor = option.feedInterceptor;
        }
        if (option?.pollingDuration) {
            this.pollingDuration = option.pollingDuration;
        }

        return this.getStreamId();
    }

    getStreamId(): Promise<void> {
        return client.StreamLongPollingController.getId()
            .then((response: string): void => {
                this.streamId = response;
                this.poll();
            })
            .catch(this.onCatch);
    }

    poll(): void {
        if (this.streamId === null || this.isPolling === true) {
            return;
        }
        this.isPolling = true;
        client.StreamLongPollingController.getFeed(this.streamId)
            .then((result: GetFeedType): void => {
                const feedwithEventName = result.data.reduce(this.transformResponseToFeed, {});

                Object.keys(feedwithEventName).forEach((eventName) => {
                    const subs = this.subscribers[eventName] || new Set();
                    const audits = feedwithEventName[eventName];
                    subs.forEach((callback) => callback(audits));
                });

                this.feedInterceptor(result);

                this.isPolling = false;
                setTimeout(() => this.poll(), this.pollingDuration);
            })
            .catch(this.onCatch);
    }

    cancelFeed(): Promise<string> {
        this.streamId = null;
        this.isPolling = false;
        return client.StreamLongPollingController.cancelFeed();
    }

    relaunchFeed(): void {
        this.isPolling = false;
        this.getStreamId();
    }

    private transformResponseToFeed(
        output: DynamicObjectType<AuditOutput[]>,
        message: AuditOutput,
    ): DynamicObjectType<AuditOutput[]> {
        const {
            base: { rootId = STREAM_EVENTS.ANY, objectType = STREAM_EVENTS.ANY },
            summary,
        } = message;

        const listEventName = [
            computeEventName({ objectType: STREAM_EVENTS.ANY }), // any event
            computeEventName({ rootId }), // any TypeEvent
            computeEventName({ objectType }), // any Id
            computeEventName({ rootId, objectType }), // specific Id and Type
        ];

        Object.keys(summary).forEach((secondaryObjectType) => {
            if (secondaryObjectType !== objectType) {
                listEventName.push(computeEventName({ objectType: secondaryObjectType }));
            }
        });

        listEventName.forEach((eventName: string) => {
            output[eventName] = output[eventName] ? [...output[eventName], message] : [message];
        });

        return output;
    }
}

export const GlobalStreamSrv = new StreamSrv();
export const GlobalStreamLongPollingSrv = new StreamLongPollingSrv();
